import React, { useState, useEffect } from "react";
import axios from "axios";
import "./QuickEnroll.css"; // Assuming a CSS file for styling

const QuickEnroll = ({ backEndUrl, platformUrl }) => {
    const [searchType, setSearchType] = useState("courseId");
    const [searchValue, setSearchValue] = useState("");
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });
    const [permissionDenied, setPermissionDenied] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const userUuid = params.get("uuid");
    const token = params.get("token");

    useEffect(() => {
        const checkUserPermissions = async () => {
            try {
                const response = await axios.get(`${platformUrl}/learn/api/public/v1/users/uuid:${userUuid}`, {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                });

                if (!response.data.institutionRoleIds.includes("ID_USER")) {
                    setPermissionDenied(true);
                }
            } catch (err) {
                console.error("Error fetching user information:", err);
                setPermissionDenied(true);
            }
        };

        checkUserPermissions();
    }, [userUuid, token, platformUrl]);

    if (permissionDenied) {
        return <h1>Permission Denied</h1>;
    }

    const handleSort = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key && sortConfig.direction === "ascending") {
            direction = "descending";
        }
        setSortConfig({ key, direction });

        setCourses(prevCourses => {
            return [...prevCourses].sort((a, b) => {
                if (a[key] < b[key]) {
                    return direction === "ascending" ? -1 : 1;
                }
                if (a[key] > b[key]) {
                    return direction === "ascending" ? 1 : -1;
                }
                return 0;
            });
        });
    };

    const handleSearch = async () => {
        if (!searchValue) {
            setError("Please enter a search value.");
            return;
        }
        setLoading(true);
        setError(null);
        setMessage(null);
        setCourses([]);

        try {
            const response = await axios.get(`${backEndUrl}/courses`, {
                params: { searchType, searchValue, platformUrl, userUuid },
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
            });

            setCourses(response.data.courses || []);
        } catch (err) {
            setError("Failed to search courses. Please try again.");
            console.error("Error searching courses:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEnroll = async (courseId) => {
        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            const response = await axios.put(
                `${backEndUrl}/memberships?courseId=${courseId}&userUuid=${userUuid}&platformUrl=${platformUrl}&action=enroll`,
                {}, // Empty body (PUT request doesn't always require a body)
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                }
            );
            setMessage(response.data.message || "Enrollment successful!");
            setCourses(prevCourses => prevCourses.map(course =>
                course.courseId === courseId ? { ...course, enrolled: "Yes" } : course
            ));
        } catch (err) {
            setError("Failed to enroll. Please try again.");
            console.error("Error enrolling in course:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleUnenroll = async (courseId) => {
        setLoading(true);
        setError(null);
        setMessage(null);

        try {
            const response = await axios.put(
                `${backEndUrl}/memberships?courseId=${courseId}&userUuid=${userUuid}&platformUrl=${platformUrl}&action=unenroll`,
                {}, // Empty body (PUT request doesn't always require a body)
                {
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": `Bearer ${token}`
                    },
                }
            );
            setMessage(response.data.message || "Unenrollment successful!");
            setCourses(prevCourses => prevCourses.map(course =>
                course.courseId === courseId ? { ...course, enrolled: "No" } : course
            ));
        } catch (err) {
            setError("Failed to unenroll. Please try again.");
            console.error("Error unenrolling from course:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="quick-enroll-container" style={{ marginLeft: "0", paddingLeft: "0" }}>
            <h1>Quick Enroll</h1>
            <p>Search for a course and enroll or unenroll directly as the Instructional Designer course role.</p>
            <div>
                <select value={searchType} onChange={(e) => setSearchType(e.target.value)}>
                    <option value="courseId">Course ID</option>
                    <option value="name">Course Name</option>
                    <option value="term">Term</option>
                    <option value="instructor">Instructor</option>
                </select>
                <input
                    type="text"
                    placeholder={`Enter ${searchType === "courseId" ? "Course ID" : searchType === "name" ? "Course Name" : searchType === "term" ? "Term" : "Instructor"}`}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                />
                <button className="search-button" onClick={handleSearch} disabled={loading}>
                    {loading ? "Searching..." : "Search"}
                </button>
            </div>
            {error && <p className="error-message">{error}</p>}
            {message && <p className="success-message">{message}</p>}
            {courses.length === 0 && !loading && <p className="no-courses-message">No Courses Found.</p>}
            <table className="course-table" style={{ borderCollapse: "collapse" }}>
                <thead>
                <tr>
                    <th style={{textAlign: "center", cursor: "pointer", width: "20%"}} onClick={() => handleSort("courseId")}>Course
                        ID {sortConfig.key === "courseId" ? (sortConfig.direction === "ascending" ? "↑" : "↓") : ""}</th>
                    <th style={{textAlign: "center", cursor: "pointer", width: "15%"}}
                        onClick={() => handleSort("enrolled")}>Enrolled {sortConfig.key === "enrolled" ? (sortConfig.direction === "ascending" ? "↑" : "↓") : ""}</th>
                    <th style={{textAlign: "center", cursor: "pointer", width: "25%"}}
                        onClick={() => handleSort("instructors")}>Instructors {sortConfig.key === "instructors" ? (sortConfig.direction === "ascending" ? "↑" : "↓") : ""}</th>
                    <th style={{textAlign: "center", width: "25%"}}>Actions</th>
                    <th style={{textAlign: "center", width: "15%"}}>Course Link</th>
                </tr>
                </thead>
                <tbody>
                {courses.map((course) => (
                    <tr key={course.id} style={{borderBottom: "1px solid #ddd", height: "50px"}}>
                        <td style={{verticalAlign: "middle", textAlign: "center"}}>{course.courseId}</td>
                        <td style={{verticalAlign: "middle", textAlign: "center"}}>{course.enrolled}</td>
                        <td>{course.instructors.map(instructor => `${instructor.name.given} ${instructor.name.family}`).join(", ")}</td>
                        <td style={{textAlign: "center", verticalAlign: "middle"}}>
                            <button className="enroll-button" onClick={() => handleEnroll(course.courseId)}
                                    disabled={loading}>
                                {loading ? "Processing..." : "Enroll"}
                            </button>
                            &nbsp;&nbsp;
                            <button className="unenroll-button" onClick={() => handleUnenroll(course.courseId)}
                                    disabled={loading}>
                                {loading ? "Processing..." : "Unenroll"}
                            </button>
                        </td>
                        <td style={{textAlign: "center", verticalAlign: "middle"}}>
                            {course.enrolled === "Yes" && course.externalAccessUrl ? (
                                <a href={course.externalAccessUrl} target="_blank" rel="noopener noreferrer">Open
                                    Course</a>
                            ) : "-"}
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
};

export default QuickEnroll;
